import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AIInterpretation
 * Auto-fetches a personalized karma interpretation from the backend LLM whenever
 * `results`, `birthDate`, or `language` changes. Shows loading, error, and the interpretation text.
 */
const AIInterpretation = ({ results, birthDate, gates, gateNames, language }) => {
  const { t, i18n } = useTranslation();
  const lang = language || i18n.language;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [meta, setMeta] = useState(null);

  const fetchInterpretation = React.useCallback(async () => {
    if (!results || !birthDate) return;
    setLoading(true);
    setError('');
    setText('');
    try {
      const res = await axios.post(`${API}/interpret`, {
        day: birthDate.day,
        month: birthDate.month,
        year: birthDate.year,
        results,
        gates: gates || null,
        gate_names: gateNames || null,
        language: lang,
      }, { timeout: 90000 });
      setText(res.data.interpretation || '');
      setMeta({ model: res.data.model, provider: res.data.provider });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || t('common.error');
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  }, [results, birthDate, gates, gateNames, lang, t]);

  // Auto-trigger on results/birthDate/language change
  useEffect(() => {
    if (results && birthDate) {
      fetchInterpretation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, birthDate?.day, birthDate?.month, birthDate?.year, lang]);

  if (!results || !birthDate) return null;

  return (
    <div className="ai-interpretation-card" data-testid="ai-interpretation">
      <div className="ai-interpretation-header">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-yellow-300" />
          <h3 className="ai-interpretation-title">{t('ai.title')}</h3>
          {meta?.model && (
            <span className="ai-interpretation-model" data-testid="ai-interpretation-model">
              {meta.model}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={fetchInterpretation}
          disabled={loading}
          className="ai-interpretation-refresh"
          title={t('ai.refreshTitle')}
          data-testid="ai-interpretation-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && (
        <div className="ai-interpretation-loading" data-testid="ai-interpretation-loading">
          <Loader2 size={18} className="animate-spin text-yellow-300" />
          <span>{t('ai.loading')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="ai-interpretation-error" data-testid="ai-interpretation-error">
          <strong>{t('ai.errorPrefix')}</strong> {error}
        </div>
      )}

      {text && !loading && (
        <div
          className="ai-interpretation-body"
          data-testid="ai-interpretation-body"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default AIInterpretation;
